import React, { useRef } from "react";
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { getShareLink, formatShareDate, getTimeLeft } from "./useShareDetails";

// re-share an existing link - same look as the "Share link is active!" step of the share modal in edit case
const ShareLinkModal = ({ row: openRow, onHide, onExtend }) => {
    // keep showing the last row while the modal fades out, otherwise the content
    // flips to the empty/"active" state for a split second on close
    const lastRowRef = useRef(null);
    if (openRow) lastRowRef.current = openRow;
    const row = openRow || lastRowRef.current;

    const shareUrl = getShareLink(row?.shareid);
    const isExpired = !!row?.isExpired;

    const handleCopyShareUrl = () => {
        navigator.clipboard.writeText(shareUrl).then(() => {
            toast.success("Link copied to clipboard!", {
                position: "bottom-center", autoClose: 2000, hideProgressBar: false,
                closeOnClick: true, pauseOnHover: true, draggable: true, theme: "light",
            });
        }).catch(() => {
            toast.error("Failed to copy link.", {
                position: "bottom-center", autoClose: 2000, hideProgressBar: false,
                closeOnClick: true, pauseOnHover: true, draggable: true, theme: "light",
            });
        });
    };

    const handleWhatsAppShare = () => {
        const text = encodeURIComponent(`View this histology image: ${shareUrl}`);
        window.open(`https://wa.me/?text=${text}`, '_blank');
    };

    return (
        <Modal show={!!openRow} onHide={onHide} centered enforceFocus={false} dialogClassName="share-modal-narrow">
            <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: "#1E222B", borderBottom: "1px solid #333" }}>
                <Modal.Title style={{ color: "#fff", fontSize: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <span className="material-symbols-outlined" style={{ color: "#F2A341", fontSize: "22px" }}>share</span>
                    Share Histology Image
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: "24px" }}>
                {isExpired ? (
                    <div>
                        <div className="d-flex align-items-center gap-2 mb-3 p-3" style={{ background: "rgba(239,68,68,0.08)", borderRadius: "8px", border: "1px solid rgba(239,68,68,0.3)" }}>
                            <span className="material-symbols-outlined" style={{ color: "#ef4444", fontSize: "20px" }}>link_off</span>
                            <div>
                                <p className="mb-0" style={{ fontWeight: 600, fontSize: "14px", color: "#991b1b" }}>This link has expired</p>
                                <p className="mb-0" style={{ fontSize: "12px", color: "#555" }}>
                                    Extend it to share this image again.
                                </p>
                            </div>
                        </div>
                        <button
                            className="btn w-100"
                            style={{ backgroundColor: "#F2A341", color: "#fff", border: "none", borderRadius: "6px", fontWeight: 700, fontSize: "14px", padding: "10px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
                            onClick={() => onExtend(row)}
                        >
                            <span className="material-symbols-outlined" style={{ fontSize: "18px" }}>update</span>
                            Extend Share Link
                        </button>
                    </div>
                ) : (
                    <div>
                        <div className="d-flex align-items-center gap-2 mb-3 p-3" style={{ background: "rgba(34,197,94,0.08)", borderRadius: "8px", border: "1px solid rgba(34,197,94,0.3)" }}>
                            <span className="material-symbols-outlined" style={{ color: "#22c55e", fontSize: "20px" }}>check_circle</span>
                            <div>
                                <p className="mb-0" style={{ fontWeight: 600, fontSize: "14px", color: "#166534" }}>Share link is active!</p>
                                {row?.expiresAt && (
                                    <p className="mb-0" style={{ fontSize: "12px", color: "#555" }}>
                                        Expires: {formatShareDate(row.expiresAt)} ({getTimeLeft(row).label})
                                    </p>
                                )}
                            </div>
                        </div>
                        <p className="text-muted mb-2" style={{ fontSize: "13px" }}>Share link:</p>
                        <input
                            type="text"
                            className="form-control mb-3"
                            value={shareUrl}
                            readOnly
                            style={{ fontSize: "13px", backgroundColor: "#f8f9fa" }}
                        />
                        <div className="d-flex gap-2">
                            <button
                                className="btn w-50"
                                style={{ backgroundColor: "#1E222B", color: "#fff", border: "none", borderRadius: "6px", fontWeight: 600, fontSize: "14px", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
                                onClick={handleCopyShareUrl}
                            >
                                <span className="material-symbols-outlined" style={{ fontSize: "18px" }}>content_copy</span>
                                Copy Link
                            </button>
                            <button
                                className="btn w-50"
                                style={{ backgroundColor: "#25D366", color: "#fff", border: "none", borderRadius: "6px", fontWeight: 600, fontSize: "14px", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
                                onClick={handleWhatsAppShare}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                WhatsApp
                            </button>
                        </div>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default ShareLinkModal;
